import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Users } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLES = [
  ["barber", "Barber"],
  ["shop", "Shop owner"],
  ["vendor", "Vendor / Brand"],
  ["educator", "Educator"],
  ["client", "Client"],
];

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [role, setRole] = useState("barber");
  const [done, setDone] = useState(false);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState(0);

  useEffect(() => {
    api.get("/community/stats").then(({ data }) => setMembers(data.members || 0)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email."); return; }
    try {
      setBusy(true);
      const { data } = await api.post("/waitlist", { email, name, city, role });
      setPos(data.position || null);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <section className="relative z-10 border-b border-white/10 bg-[#050505]">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <div className="border border-[#D4AF37]/30 bg-gradient-to-b from-[#111] to-[#0a0a0a] p-8 md:p-12 gold-glow">
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">Founding Members</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold max-w-2xl">Get in early. Lock founding-member perks.</h2>
          <p className="mt-4 text-neutral-300 max-w-xl">Join the list to claim early access, discounted founding rates, and first shot at Quality Certification in your city. No payment now — just your spot.</p>
          {members > 0 && (
            <p className="mt-4 flex items-center gap-2 text-sm text-[#D4AF37]" data-testid="waitlist-count">
              <Users size={15} /> {members}+ already claimed their spot
            </p>
          )}

          {done ? (
            <div className="mt-8 flex items-center gap-3 text-[#D4AF37]" data-testid="waitlist-success">
              <CheckCircle2 size={26} />
              <div>
                <p className="font-display text-xl font-semibold">You're on the list.</p>
                <p className="text-sm text-neutral-400">{pos ? `You're #${pos} in line. ` : ""}We'll reach out with your founding-member invite.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="waitlist-form">
              <div className="flex flex-wrap gap-2">
                {ROLES.map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setRole(v)} data-testid={`waitlist-role-${v}`}
                    className={`px-3 py-1.5 text-sm border transition-colors ${role === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="waitlist-name" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" data-testid="waitlist-city" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="waitlist-email" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
              </div>
              <Button type="submit" disabled={busy} data-testid="waitlist-submit" className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 text-base group">
                {busy ? "Joining…" : <>Claim My Spot <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" /></>}
              </Button>
              <p className="text-xs text-neutral-500">No spam. We'll only email you about your founding-member invite and launch.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
